// Create: src/contexts/FavoritesContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';

const FavoritesContext = createContext();
const GUEST_WISHLIST_KEY = 'guestWishlist';
const FAVORITES_CACHE_PREFIX = 'userFavoritesCache:';
const FAVORITES_SELECT =
  'id, product_id, product_name, product_image, product_price, product_category, created_at';

const readGuestWishlist = () => {
  if (typeof window === 'undefined') return [];

  try {
    const stored = window.localStorage.getItem(GUEST_WISHLIST_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error reading guest wishlist:', error);
    return [];
  }
};

const writeGuestWishlist = (items) => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(GUEST_WISHLIST_KEY, JSON.stringify(items));
  } catch (error) {
    console.error('Error writing guest wishlist:', error);
  }
};

const readCachedFavorites = (userId) => {
  if (!userId || typeof window === 'undefined') return null;

  try {
    const stored = window.sessionStorage.getItem(`${FAVORITES_CACHE_PREFIX}${userId}`);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error reading favorites cache:', error);
    return null;
  }
};

const writeCachedFavorites = (userId, items) => {
  if (!userId || typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(`${FAVORITES_CACHE_PREFIX}${userId}`, JSON.stringify(items));
  } catch (error) {
    console.error('Error writing favorites cache:', error);
  }
};

const normalizeProductId = (product) => {
  const raw = product?.product_id ?? product?.id;
  if (raw == null || raw === '') {
    throw new Error('Invalid product. Please refresh and try again.');
  }

  const numeric = Number(raw);
  if (Number.isFinite(numeric)) return numeric;

  throw new Error('Invalid product. Please refresh and try again.');
};

const toFavoriteRecord = (product) => ({
  product_id: normalizeProductId(product),
  product_name: product.product_name || product.name,
  product_image: product.product_image || product.image,
  product_price: product.product_price ?? product.price ?? null,
  product_category: product.product_category || product.category || null,
});

const mapFavoriteRecord = (favorite) => ({
  product_id: favorite.product_id,
  product_name: favorite.product_name,
  product_image: favorite.product_image,
  product_price: favorite.product_price,
  product_category: favorite.product_category,
});

const formatFavoriteError = (error, fallback = 'Failed to update wishlist') => {
  if (!error) return fallback;

  const message = error.message || '';
  if (
    message === 'Failed to fetch' ||
    message.includes('NetworkError') ||
    error.name === 'TypeError'
  ) {
    return 'Unable to reach the server. Check your connection and try again.';
  }
  if (message.includes('JWT') || error.code === 'PGRST301') {
    return 'Session expired. Please sign in again.';
  }
  if (
    error.code === '42501' ||
    message.toLowerCase().includes('row-level security') ||
    message.toLowerCase().includes('permission denied')
  ) {
    return 'Could not access your wishlist. Try signing out and back in.';
  }
  if (process.env.NODE_ENV === 'development') {
    return message + (error.code ? ` (${error.code})` : '');
  }
  return message || fallback;
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

export const FavoritesProvider = ({ children }) => {
  const [favorites, setFavorites] = useState(() => readGuestWishlist());
  const [loading, setLoading] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const loadedUserIdRef = useRef(null);
  const inFlightUserIdRef = useRef(null);
  const requestIdRef = useRef(0);

  const syncFavoritesCache = useCallback((userId, items) => {
    if (!userId) return;
    writeCachedFavorites(userId, items);
    loadedUserIdRef.current = userId;
  }, []);

  const loadFavoritesFromDatabase = useCallback(async (userId) => {
    const { data, error } = await supabase
      .from('user_favorites')
      .select(FAVORITES_SELECT)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(mapFavoriteRecord);
  }, []);

  const saveFavoriteToDatabase = useCallback(async (favorite) => {
    const {
      data: { user: authUser },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !authUser) {
      throw new Error('Please sign in again to save your wishlist.');
    }

    const { data: existing, error: existingError } = await supabase
      .from('user_favorites')
      .select(FAVORITES_SELECT)
      .eq('user_id', authUser.id)
      .eq('product_id', favorite.product_id)
      .maybeSingle();

    if (existingError) throw existingError;
    if (existing) {
      return { favorite: mapFavoriteRecord(existing), alreadySaved: true };
    }

    const { data: inserted, error: insertError } = await supabase
      .from('user_favorites')
      .insert({
        user_id: authUser.id,
        ...favorite,
      })
      .select(FAVORITES_SELECT)
      .single();

    if (insertError) throw insertError;
    return { favorite: mapFavoriteRecord(inserted), alreadySaved: false };
  }, []);

  const removeFavoriteFromDatabase = useCallback(async (productId) => {
    const {
      data: { user: authUser },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !authUser) {
      throw new Error('Please sign in again.');
    }

    const { error } = await supabase
      .from('user_favorites')
      .delete()
      .eq('user_id', authUser.id)
      .eq('product_id', productId);

    if (error) throw error;
  }, []);

  const clearFavoritesFromDatabase = useCallback(async () => {
    const {
      data: { user: authUser },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !authUser) {
      throw new Error('Please sign in again.');
    }

    const { error } = await supabase
      .from('user_favorites')
      .delete()
      .eq('user_id', authUser.id);

    if (error) throw error;
  }, []);

  const loadFavorites = useCallback(async ({ force = false } = {}) => {
    if (!user) return;

    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (sessionError || !session?.user) {
      console.error('Error reading auth session for wishlist:', sessionError);
      return;
    }

    const userId = session.user.id;

    if (!force && loadedUserIdRef.current === userId) return;
    if (inFlightUserIdRef.current === userId) return;

    const cached = readCachedFavorites(userId);
    if (cached !== null) {
      setFavorites(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }

    const requestId = ++requestIdRef.current;
    inFlightUserIdRef.current = userId;

    try {
      const guestWishlist = readGuestWishlist();
      if (guestWishlist.length > 0) {
        const migratedFavorites = [];
        for (const favorite of guestWishlist) {
          const result = await saveFavoriteToDatabase(toFavoriteRecord(favorite));
          migratedFavorites.push(result.favorite);
        }
        if (migratedFavorites.length > 0) {
          writeGuestWishlist([]);
        }
      }

      if (requestId !== requestIdRef.current) return;
      const items = await loadFavoritesFromDatabase(userId);
      setFavorites(items);
      syncFavoritesCache(userId, items);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error('Error loading favorites:', error);
      if (cached === null) {
        setFavorites([]);
      }
      toast.error(formatFavoriteError(error, 'Failed to load wishlist'));
    } finally {
      if (requestId === requestIdRef.current) {
        inFlightUserIdRef.current = null;
        setLoading(false);
      }
    }
  }, [user, syncFavoritesCache, loadFavoritesFromDatabase, saveFavoriteToDatabase]);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      requestIdRef.current += 1;
      inFlightUserIdRef.current = null;
      loadedUserIdRef.current = null;
      setFavorites(readGuestWishlist());
      setLoading(false);
      return;
    }

    loadFavorites();
  }, [user, authLoading, loadFavorites]);

  const addToFavorites = async (product) => {
    if (!user) {
      const favoriteData = toFavoriteRecord(product);
      const guestWishlist = readGuestWishlist();
      const alreadySaved = guestWishlist.some(
        (item) => Number(item.product_id) === Number(favoriteData.product_id)
      );

      if (alreadySaved) {
        toast.info('Already saved on this device');
        return false;
      }

      const nextGuestWishlist = [favoriteData, ...guestWishlist];
      writeGuestWishlist(nextGuestWishlist);
      setFavorites(nextGuestWishlist);
      toast.success('Saved to wishlist on this device');
      return true;
    }

    try {
      const result = await saveFavoriteToDatabase(toFavoriteRecord(product));
      if (result.alreadySaved) {
        setFavorites((prev) => {
          const normalizedId = Number(result.favorite.product_id);
          const alreadyInState = prev.some(
            (fav) => Number(fav.product_id) === normalizedId
          );
          if (alreadyInState) return prev;
          const next = [
            result.favorite,
            ...prev.filter((fav) => Number(fav.product_id) !== normalizedId),
          ];
          syncFavoritesCache(user.id, next);
          return next;
        });
        toast.info('Item already in wishlist');
        return true;
      }
      setFavorites((prev) => {
        const next = [result.favorite, ...prev.filter((fav) => fav.product_id !== result.favorite.product_id)];
        syncFavoritesCache(user.id, next);
        return next;
      });
      toast.success('Saved to wishlist');
      return true;
    } catch (error) {
      console.error('Error adding to favorites:', error);
      toast.error(formatFavoriteError(error, 'Failed to save to wishlist'));
      return false;
    }
  };

  const removeFromFavorites = async (productId) => {
    const normalizedProductId = Number(productId);

    if (!user) {
      const nextGuestWishlist = readGuestWishlist().filter(
        (item) => Number(item.product_id) !== normalizedProductId
      );
      writeGuestWishlist(nextGuestWishlist);
      setFavorites(nextGuestWishlist);
      toast.success('Removed from wishlist');
      return true;
    }

    try {
      await removeFavoriteFromDatabase(normalizedProductId);

      setFavorites((prev) => {
        const next = prev.filter((fav) => Number(fav.product_id) !== normalizedProductId);
        syncFavoritesCache(user.id, next);
        return next;
      });

      toast.success('Removed from wishlist');
      return true;
    } catch (error) {
      console.error('Error removing from favorites:', error);
      toast.error(formatFavoriteError(error, 'Failed to remove from wishlist'));
      return false;
    }
  };

  const isFavorite = (productId) => {
    const normalizedProductId = Number(productId);
    return favorites.some((fav) => Number(fav.product_id) === normalizedProductId);
  };

  const toggleFavorite = async (product) => {
    const productId = product?.product_id ?? product?.id;
    if (isFavorite(productId)) {
      return await removeFromFavorites(productId);
    }
    return await addToFavorites(product);
  };

  const getFavoriteCount = () => favorites.length;

  const clearFavorites = async () => {
    if (!user) {
      writeGuestWishlist([]);
      setFavorites([]);
      toast.success('Wishlist cleared on this device');
      return;
    }

    try {
      await clearFavoritesFromDatabase();

      setFavorites([]);
      syncFavoritesCache(user.id, []);
      toast.success('Wishlist cleared');
    } catch (error) {
      console.error('Error clearing favorites:', error);
      toast.error(formatFavoriteError(error, 'Failed to clear wishlist'));
    }
  };

  const value = {
    favorites,
    loading: authLoading || loading,
    addToFavorites,
    removeFromFavorites,
    isFavorite,
    toggleFavorite,
    getFavoriteCount,
    clearFavorites,
    loadFavorites: () => loadFavorites({ force: true }),
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};
